import { Client, Controller, Endpoint, mapUrl } from "@/repository";
import { CallSession } from "@/common/models/callSession";
import {
	OutboundCallRequest,
	OutboundCallResponse,
	TrustedEndpoint,
} from "../models/outboundCall";
import PlaySoundHelper, { SoundType } from "@/common/helpers/play-sound-helper";
import { AssignInteractionRequest } from "../models/assignInteraction";
import { WebchatVideoOutboundResponse } from "../models/webchatCallRequest";

export const VoiceEndpointsKey = "VoiceTrustedEndpoints";

export const fetchCurrentCallSession = (): Promise<CallSession> => {
	return Client.instance.get(Controller.AgentCall);
};

export const deleteCall = (): Promise<any> => {
	return Client.instance.post(mapUrl(Controller.AgentCall, Endpoint.Terminate));
};

export const setCrmSelectedCases = (
	selectedCaseIds: string[]
): Promise<any> => {
	return Client.instance.post(
		mapUrl(Controller.AgentCall, Endpoint.SalesforceCases),
		{ selectedCaseIds }
	);
};

export const pauseCall = (): Promise<any> => {
	return Client.instance.post(mapUrl(Controller.AgentCall, Endpoint.Pause));
};

export const resumeCall = (): Promise<any> => {
	return Client.instance.post(mapUrl(Controller.AgentCall, Endpoint.Resume));
};

export const parkCall = (): Promise<any> => {
	return Client.instance.post(mapUrl(Controller.AgentCall, Endpoint.Park));
};

export const unParkCall = (sessionId: string): Promise<any> => {
	const url = mapUrl(Controller.AgentCall, Endpoint.UnPark);
	return Client.instance.post(url, sessionId);
};

export const pickUpCall = (scenarioId: string): Promise<any> => {
	const url = mapUrl(Controller.AgentCall, Endpoint.PickUp);
	return Client.instance.post(url, scenarioId);
};

export const assignCall = (data: AssignInteractionRequest): Promise<any> => {
	const url = mapUrl(Controller.AgentCall, Endpoint.Assign);
	return Client.instance.post(url, data);
};

export const fetchVoiceTrustedEndpoints = (): Promise<TrustedEndpoint[]> => {
	return Client.instance.get(
		mapUrl(Controller.AgentCall, Endpoint.TrustedEndpoints)
	);
};

export const outboundCall = (
	data: OutboundCallRequest
): Promise<OutboundCallResponse> => {
	const url = mapUrl(Controller.AgentCall, Endpoint.Outbound);

	return Client.instance.post(url, data);
};

export const startWebchatVideoOutbound = (
	conversationId: string,
	requestId: string
): Promise<WebchatVideoOutboundResponse> => {
	const url = mapUrl(Controller.AgentCall, Endpoint.WebchatVideoOutbound);

	return Client.instance.post(url, { conversationId, requestId });
};

export const sendTone = (data: string): Promise<any> => {
	const url = mapUrl(Controller.AgentCall, Endpoint.Tone);
	PlaySoundHelper.playSound(SoundType.Tone);

	return Client.instance.post(url, JSON.stringify(data));
};

export const acceptSimultaneouslyOfferedCall = (
	sessionId: string
): Promise<any> => {
	const url = mapUrl(
		Controller.AgentCall,
		Endpoint.AcceptSimultaneouslyOfferedCall
	);
	return Client.instance.post(url, sessionId);
};

export const rejectSimultaneouslyOfferedCall = (
	sessionId: string
): Promise<any> => {
	const url = mapUrl(
		Controller.AgentCall,
		Endpoint.RejectSimultaneouslyOfferedCall
	);
	return Client.instance.post(url, sessionId);
};

export const startTranscriptionCall = (language: string): Promise<any> => {
	return Client.instance.post(
		mapUrl(Controller.AgentCall, Endpoint.TranscriptionStart),
		language
	);
};

export const updateTranscriptionCall = (language: string): Promise<any> => {
	return Client.instance.post(
		mapUrl(Controller.AgentCall, Endpoint.TranscriptionUpdate),
		language
	);
};

export const stopTranscriptionCall = (): Promise<any> => {
	return Client.instance.post(
		mapUrl(Controller.AgentCall, Endpoint.TranscriptionStop)
	);
};

export const startRecordingCall = (): Promise<any> => {
	return Client.instance.post(
		mapUrl(Controller.AgentCall, Endpoint.RecordingStart)
	);
};

export const stopRecordingCall = (): Promise<any> => {
	return Client.instance.post(
		mapUrl(Controller.AgentCall, Endpoint.RecordingStop)
	);
};

export const startMuteCall = (): Promise<any> => {
	return Client.instance.post(mapUrl(Controller.AgentCall, Endpoint.MuteStart));
};

export const stopMuteCall = (): Promise<any> => {
	return Client.instance.post(mapUrl(Controller.AgentCall, Endpoint.MuteStop));
};
